import moment from "moment";
import { create } from "zustand";
import { TStatistics } from "../../api/statistics/types";
import { TStatisticsCard } from "./types";
import {
  cardBuilder,
  companiesCard,
  companySubscriptionsUpdate,
  ContractCardBuilder,
  contractQuantity,
  deviceDataCards,
  dotDataQuantity,
  driversStatsCard,
  subscriptionQuantity,
  userDataCards,
  vehicleAndDriversCard,
} from "./utils";

interface AppState {
  activeOption: { title: string; value: string } | null;
  startDate: Date | null;
  endDate: Date | null;

  driverData: TStatisticsCard | null;
  driverStatsData: TStatisticsCard | null;
  vehicleData: TStatisticsCard | null;
  vehicleStatsData: TStatisticsCard | null;
  companyData: TStatisticsCard | null;
  dotData: TStatisticsCard | null;
  contractData: TStatisticsCard | null;
  subscriptionData: TStatisticsCard | null;
  subscriptionCompanyData: TStatisticsCard | null;
  userData: TStatisticsCard | null;
  deviceData: TStatisticsCard | null;

  activeTabIndex: number;
  topCount: number;
  companyTotals: { title: string; total: number }[] | null;

  isLoadingData: boolean;

  setActiveOption: (option: { title: string; value: string }) => void;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;

  setDriverData: (data: TStatistics) => void;
  setVehicleData: (data: TStatistics) => void;
  setCompanyData: (data: TStatistics) => void;
  setDotData: (data: TStatistics) => void;
  setContractData: (data: TStatistics) => void;
  setSubscriptionData: (data: TStatistics) => void;
  setSubscriptionCompanyData: (data: TStatistics) => void;
  setUserData: (data: TStatistics) => void;
  setDeviceData: (data: TStatistics) => void;

  setIsLoadingData: (isLoading: boolean) => void;
  handleTabChange: (index: number) => void;
  setTopCount: (num: number) => void;

  setAll: (data: Record<string, TStatistics>) => void;
  resetStore: () => void;
}

const useStore = create<AppState>()((set) => ({
  activeOption: { title: "Week", value: "week" },
  startDate: moment().startOf("day").subtract(1, "week").toDate(),
  endDate: moment().endOf("day").toDate(),

  driverData: null,
  driverStatsData: null,
  vehicleData: null,
  vehicleStatsData: null,
  companyData: null,
  dotData: null,
  contractData: null,
  subscriptionData: null,
  subscriptionCompanyData: null,
  userData: null,
  deviceData: null,

  isLoadingData: false,
  companyTotals: null,
  activeTabIndex: 0,
  topCount: 5,

  setActiveOption: (option) => set({ activeOption: option }),
  setStartDate: (date) => set({ startDate: date }),
  setEndDate: (date) => set({ endDate: date }),

  setDriverData: (data) => {
    set({
      driverData: cardBuilder(
        data,
        "Drivers",
        "Total drivers",
        vehicleAndDriversCard(data, true),
        ["activeTotal", "inactiveTotal", "newTotal", "codriverTotal"],
      ),
      driverStatsData: cardBuilder(
        data,
        "Activities",
        "",
        driversStatsCard(data, false),
        [
          "activeUniqueActionTotal",
          "inactiveUniqueActionTotal",
          "activeActionTotal",
          "inactiveActionTotal",
        ],
      ),
    });
  },

  setVehicleData: (data) => {
    set({
      vehicleData: cardBuilder(
        data,
        "Vehicles",
        "Total vehicles",
        vehicleAndDriversCard(data),
        ["activeTotal", "inactiveTotal", "newTotal"],
      ),
      vehicleStatsData: cardBuilder(
        data,
        "Activities",
        "",
        driversStatsCard(data, true),
        [
          "activeUniqueActionTotal",
          "inactiveUniqueActionTotal",
          "activeActionTotal",
          "inactiveActionTotal",
        ],
      ),
    });
  },

  setCompanyData: (data) => {
    set({
      companyData: cardBuilder(
        data,
        "Companies",
        "Total companies",
        companiesCard(data),
        [
          "activeTotal",
          "inactiveTotal",
          "trialTotal",
          "newTotal",
          "cancelledTotal",
        ],
      ),
    });
  },

  setDotData: (data) => {
    set({
      dotData: cardBuilder(
        data,
        "DOT Statistics",
        "Total DOT Inspections",
        dotDataQuantity(data),
        ["total"],
      ),
    });
  },

  setContractData: (data) => {
    set({
      contractData: ContractCardBuilder(
        data,
        "Subscriptions",
        "Total quantity",
        contractQuantity(data),
        ["activeQuantityTotal", "contractQuantityTotal", "contractTotal"],
      ),
    });
  },
  setSubscriptionData: (data) => {
    set({
      subscriptionData: cardBuilder(
        data,
        "Subscription Updates",
        "Total quantity",
        subscriptionQuantity(data),
        ["increaseTotal", "deacreaseTotal", "updateTotal"],
      ),
    });
  },

  setSubscriptionCompanyData: (data) => {
    set({
      subscriptionCompanyData: cardBuilder(
        data,
        "Company subscription updates",
        "Total subscription updated companies",
        companySubscriptionsUpdate(data),
        ["increaseCompanyTotal", "decreaseCompanyTotal", "updateCompanyTotal"],
      ),
    });
  },

  setUserData: (data) => {
    set({
      userData: cardBuilder(
        data,
        "User Engagement Metrics",
        "Total active users",
        userDataCards(data),
        [
          "loginTotal",
          "logoutTotal",
          "acceptedTotal",
          "rejectedTotal",
          "newTotal",
        ],
      ),
    });
  },

  setDeviceData: (data) => {
    set({
      deviceData: cardBuilder(
        data,
        "App Users",
        "Total active users",
        deviceDataCards(data),
        ["androidTotal", "iosTotal", "mobileTotal", "tabletTotal"],
      ),
    });
  },
  setAll: (data: Record<string, TStatistics>) => {
    const driverData = data.driverData;
    const vehicleData = data.vehicleData;

    const drStatsData: TStatisticsCard = cardBuilder(
      { ...driverData, totalDifference: 0, totalPercentage: 0 },
      "Drivers Activities",
      "",
      driversStatsCard(driverData, true, true),
      [
        "newTotal",
        "activeUniqueActionTotal",
        "inactiveUniqueActionTotal",
        "activeActionTotal",
        "inactiveActionTotal",
      ],
    );
    const vehic: TStatisticsCard = cardBuilder(
      { ...vehicleData, totalDifference: 0, totalPercentage: 0 },
      "Vehicles Activities",
      "",
      driversStatsCard(vehicleData, true, true),
      [
        "newTotal",
        "activeUniqueActionTotal",
        "inactiveUniqueActionTotal",
        "activeActionTotal",
        "inactiveActionTotal",
      ],
    );
    const subscriptionData: TStatisticsCard = cardBuilder(
      { ...data.subscriptionData, totalDifference: 0, totalPercentage: 0 },
      "Subscription",
      "Total quantity",
      subscriptionQuantity(data.subscriptionData),
      ["increaseTotal", "deacreaseTotal", "updateTotal"],
    );

    const userData: TStatisticsCard = cardBuilder(
      { ...data.userData, totalDifference: 0, totalPercentage: 0 },
      "User Engagement Metrics",
      "Total active users",
      userDataCards(data.userData),
      [
        "loginTotal",
        "logoutTotal",
        "acceptedTotal",
        "rejectedTotal",
        "newTotal",
      ],
    );

    const allTotals = [
      { title: "Total Active Dispatchers", total: data.userData.total },
      { title: "Total Drivers", total: data.driverData.total },
      { title: "Active Drivers", total: data.driverData.activeTotal },
      { title: "Inactive Drivers", total: data.driverData.inactiveTotal },
      { title: "Total Trucks", total: data.vehicleData.total },
      { title: "Active Trucks", total: data.vehicleData.activeTotal },
      { title: "Inactive Trucks", total: data.vehicleData.inactiveTotal },
      { title: "Subscription Quantity", total: data.subscriptionData.total },
      { title: "Total Active App Users", total: data.deviceData?.total || 0 },
      {
        title: "Total Android Users",
        total: data.deviceData?.androidTotal || 0,
      },
      { title: "Total IOS Users", total: data.deviceData?.iosTotal || 0 },
      { title: "Total Mobile Users", total: data.deviceData?.mobileTotal || 0 },
      { title: "Total Tablet Users", total: data.deviceData?.tabletTotal || 0 },
    ];

    set({
      driverStatsData: drStatsData || null,
      vehicleStatsData: vehic || null,
      subscriptionData: subscriptionData || null,
      userData: userData || null,
      companyTotals: allTotals,
    });
  },

  setIsLoadingData: (isLoading) => set({ isLoadingData: isLoading }),
  handleTabChange: (index) => set({ activeTabIndex: index }),
  setTopCount: (num) => set({ topCount: num }),

  resetStore: () =>
    set({
      activeOption: { title: "Week", value: "week" },
      startDate: moment().startOf("day").subtract(1, "week").toDate(),
      endDate: moment().endOf("day").toDate(),

      driverData: null,
      driverStatsData: null,
      vehicleData: null,
      vehicleStatsData: null,
      companyData: null,
      dotData: null,
      contractData: null,
      subscriptionData: null,
      subscriptionCompanyData: null,
      userData: null,
      deviceData: null,

      isLoadingData: false,
      activeTabIndex: 0,
      topCount: 5,
    }),
}));

export default useStore;
